import { alpha, Box, Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import MotivationIllustration from "src/assets/illustrations/motivation-illustration";
import Iconify from "src/components/iconify";
import { useDismissOnboardBannerMutation, useDeleteBannerMutation, useGetBannersQuery } from "src/context/api/onboard/api";
import { Banner, Translations } from "src/context/api/onboard/models";
import { bgGradient } from "src/theme/css";
import AddBannerForm from "./add-form";
import { dashboardPaths } from "src/routes/dashboardPaths";
import { toTitleCase } from "./add-form";
import { useLocales } from "src/locales";

export default function BannersGrid({ banners, edit = false, dismiss = true, onDismiss }: { banners: Banner[], edit?: boolean, dismiss?: boolean, onDismiss?: () => void }) {
    const theme = useTheme();
    const { t, addTranslation, currentLang } = useLocales();
    const [dismissBanner] = useDismissOnboardBannerMutation();
    const [bannersState, setBannersState] = useState<Banner[]>(banners);
    const [deleteBanner] = useDeleteBannerMutation();
    const [isEditing, setIsEditing] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);

    useEffect(() => {
        setBannersState(edit ? banners : [].concat(banners as any).sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0)).filter((banner: any) => banner.target === window.location.pathname));
    }, [banners]);

    return (
        <Box sx={{ width: '100%', mt: 1 }}>
            {bannersState?.map((banner) => (
                <Box mb={2}>
                    {edit && (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                px: 2,
                                py: 1,
                                backdropFilter: 'blur(8px)',
                                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.64),
                            }}
                        >
                            <Typography variant="h4" sx={{ color: 'common.white' }}>
                                {toTitleCase(Object.keys(dashboardPaths).find((key) => dashboardPaths[key as keyof typeof dashboardPaths] === banner.target) || 'Unknown')}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    onClick={() => {
                                        setIsEditing(true);
                                        setSelectedBanner(banner);
                                    }}
                                    sx={{ color: 'common.white' }}
                                    size="small"
                                >
                                    <Iconify icon="mdi:pencil" />
                                </IconButton>
                                <IconButton
                                    onClick={() => deleteBanner(banner.id).unwrap().then(() => {
                                        setBannersState(bannersState.filter((b) => b.id !== banner.id));
                                    })}
                                    sx={{ color: 'common.white' }}
                                    size="small"
                                >
                                    <Iconify icon="mdi:delete" />
                                </IconButton>
                            </Stack>
                        </Stack>
                    )}
                    {isEditing && selectedBanner?.id === banner.id ? (
                        <AddBannerForm onClose={() => {
                            setIsEditing(false);
                            setSelectedBanner(null);
                            onDismiss?.();
                        }} {...selectedBanner} />
                    )
                        :
                        <>
                            <Box
                                sx={{
                                    height: 364,
                                    position: 'relative',
                                    p: 5,
                                    border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.grey[800]}` : "1px solid rgba(145, 158, 171, 0.2)",
                                    borderRadius: 1,
                                    bgcolor: 'white',
                                    overflow: 'hidden',
                                    ...bgGradient({
                                        direction: '135deg',
                                        color: theme.palette.mode === 'dark' ? `to right, ${theme.palette.grey[900]} 25%, ${alpha(theme.palette.primary.darker, 0.88)}` : 'undefined',
                                        imgUrl: '/assets/background/background-6.webp'
                                    }),
                                }}
                            >
                                {!edit && dismiss && <IconButton
                                    onClick={() => dismissBanner(banner.id).unwrap().then(() => {
                                        setBannersState(bannersState.filter((b) => b.id !== banner.id));
                                        onDismiss?.();
                                    })}
                                    sx={{
                                        position: 'absolute',
                                        top: theme.spacing(1),
                                        right: theme.spacing(1),
                                        color: theme.palette.mode === 'dark' ? 'common.white' : "black",
                                    }}
                                >
                                    <Iconify icon={"mdi:times"} />
                                </IconButton>
                                }
                                <Stack justifyContent="space-between" direction="row" spacing={2}>

                                    <Stack p={2} direction="column" spacing={2}>

                                        <Typography variant="h6">{banner.translations?.[currentLang.value as any]?.title}</Typography>
                                        <Typography variant="body2">
                                            <ReactMarkdown>
                                                {banner.translations?.[currentLang.value as any]?.content || ''}
                                            </ReactMarkdown>
                                        </Typography>
                                        <Box flexGrow={1} />
                                        <Stack direction="row" spacing={2}>
                                            {banner.translations?.[currentLang.value as any]?.ctas?.map((cta: any, index: any) => (
                                                cta.label && (
                                                    <Button
                                                        key={index}
                                                        variant={cta.type === 'primary' ? 'contained' : 'outlined'}
                                                        size="small"
                                                        href={cta.url || '#'}
                                                        target="_blank"
                                                    >
                                                        {cta.label}
                                                    </Button>
                                                )
                                            ))}
                                        </Stack>
                                    </Stack>

                                    {banner.image_url && (
                                        <Box
                                            component="img"
                                            src={banner.image_url}
                                            alt="Banner"
                                            alignSelf={banner.image_placement === 'center' ? 'center' : banner.image_placement === 'left' ? 'flex-start' : 'flex-end'}
                                            sx={{
                                                width: banner.image_placement === 'center' ? '100%' : "auto",
                                                height: 200,
                                                borderRadius: 1,
                                                borderTopLeftRadius: banner.image_placement === 'left' ? 5 : 0,
                                                borderTopRightRadius: banner.image_placement === 'right' ? 5 : 0,
                                                borderBottomLeftRadius: banner.image_placement === 'left' ? 5 : 0,
                                                borderBottomRightRadius: banner.image_placement === 'right' ? 5 : 0,
                                                m: 0
                                            }}
                                        />
                                    )}
                                    {
                                        !banner.image_url && (
                                            <MotivationIllustration />
                                        )
                                    }
                                </Stack>
                            </Box>
                        </>
                    }
                </Box>
            ))}
        </Box>
    )
}